/**
 * useAiSearchStream Hook
 *
 * React hook for streaming AI Search responses via SSE.
 * Handles connection, parsing, and state management for real-time streaming.
 */

import { useState, useCallback, useRef } from 'react';

// Stream event types (must match server types)
export type StreamEventType = 'thinking' | 'tool' | 'delta' | 'correction' | 'explanation' | 'done' | 'error';

export interface StreamEvent {
  type: StreamEventType;
  timestamp: number;
  payload?: unknown;
}

export interface ToolPayload {
  name: string;
  status: 'running' | 'done';
  count?: number;
  /** Elapsed time in milliseconds since query started */
  elapsedMs?: number;
}

export interface DeltaPayload {
  content: string;
}

export interface DonePayload {
  conversationId: string;
  sources: Array<{
    type: string;
    id: string;
    title: string;
    snippet?: string;
  }>;
  detectedLanguage?: string;
  tokenUsage?: {
    promptTokens: number;
    completionTokens: number;
    totalTokens: number;
  };
  /** Complete metrics */
  metrics?: {
    [key: string]: unknown;
  };
}

export interface ErrorPayload {
  message: string;
}

// Explanation types (must match server types)
export interface FilterExplanation {
  field: string;
  fieldLabel: string;
  operator: string;
  operatorLabel: string;
  value: string;
  valueLabel?: string;
  relationMode?: 'some' | 'every' | 'none';
}

export interface AggregationExplanation {
  type: 'count' | 'groupBy' | 'countRelation' | 'groupByCount';
  field?: string;
  fieldLabel?: string;
  having?: {
    operator: string;
    operatorLabel: string;
    value: number;
  };
}

export interface ToolStepExplanation {
  tool: string;
  toolLabel: string;
  action: string;
  entity: string;
  entityLabel: string;
  filters?: FilterExplanation[];
  aggregation?: AggregationExplanation;
  result: string;
  resultCount?: number;
  hasMore?: boolean;
  durationMs: number;
}

export interface QueryExplanation {
  summary: string;
  steps: ToolStepExplanation[];
  totalTools: number;
  totalDurationMs: number;
  tokenUsage?: {
    promptTokens: number;
    completionTokens: number;
    totalTokens: number;
  };
}

export interface ExplanationPayload extends QueryExplanation {}

export type StreamStatus = 'idle' | 'connecting' | 'streaming' | 'done' | 'error';

export interface StreamState {
  status: StreamStatus;
  content: string;
  toolStatus: ToolPayload | null;
  conversationId: string | null;
  /** Response ID for this specific response (for feedback tracking) */
  responseId: string | null;
  sources: DonePayload['sources'];
  explanation: QueryExplanation | null;
  tokenUsage: { promptTokens: number; completionTokens: number; totalTokens: number } | null;
  error: string | null;
}

interface UseAiSearchStreamOptions {
  onEvent?: (event: StreamEvent) => void;
  onComplete?: (state: StreamState) => void;
  onError?: (error: string) => void;
}

// Generate UUID (browser-compatible)
function generateUUID(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // Fallback for older browsers
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export function useAiSearchStream(options: UseAiSearchStreamOptions = {}) {
  const [state, setState] = useState<StreamState>({
    status: 'idle',
    content: '',
    toolStatus: null,
    conversationId: null,
    responseId: null,
    sources: [],
    explanation: null,
    tokenUsage: null,
    error: null,
  });

  const abortControllerRef = useRef<AbortController | null>(null);

  const reset = useCallback(() => {
    setState({
      status: 'idle',
      content: '',
      toolStatus: null,
      conversationId: null,
      responseId: null,
      sources: [],
      explanation: null,
      tokenUsage: null,
      error: null,
    });
  }, []);

  const abort = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }, []);

  const sendMessage = useCallback(
    async (query: string, token: string, conversationId?: string | null) => {
      // Abort any existing request
      abort();

      // Generate response ID for tracking feedback
      const responseId = generateUUID();

      // Reset state
      setState({
        status: 'connecting',
        content: '',
        toolStatus: null,
        conversationId: conversationId || null,
        responseId,
        sources: [],
        explanation: null,
        tokenUsage: null,
        error: null,
      });

      const controller = new AbortController();
      abortControllerRef.current = controller;

      try {
        const response = await fetch('/api/ai-search/stream', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ query, conversationId, responseId }),
          signal: controller.signal,
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          const errorMessage = errorData.error || `HTTP ${response.status}`;
          setState((prev) => ({
            ...prev,
            status: 'error',
            error: errorMessage,
          }));
          options.onError?.(errorMessage);
          return;
        }

        if (!response.body) {
          setState((prev) => ({
            ...prev,
            status: 'error',
            error: 'No response body',
          }));
          return;
        }

        setState((prev) => ({ ...prev, status: 'streaming' }));

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let finalState: StreamState | null = null;

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });

          // Parse SSE events from buffer
          const lines = buffer.split('\n');
          buffer = lines.pop() || ''; // Keep incomplete line in buffer

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              const data = line.slice(6);

              // Check for end signal
              if (data === '[DONE]') {
                if (finalState) {
                  setState(finalState);
                  options.onComplete?.(finalState);
                }
                continue;
              }

              try {
                const event: StreamEvent = JSON.parse(data);
                options.onEvent?.(event);

                // Capture state synchronously from updater to avoid no-loop-func warning
                // (setState updater runs sync; we read the new state into a local const)
                let capturedNewState: StreamState | null = null;
                setState((prev) => {
                  const newState = { ...prev };

                  switch (event.type) {
                    case 'thinking':
                      newState.status = 'streaming';
                      newState.toolStatus = null;
                      break;

                    case 'tool':
                      newState.toolStatus = event.payload as ToolPayload;
                      break;

                    case 'delta':
                      const deltaPayload = event.payload as DeltaPayload;
                      newState.content += deltaPayload.content;
                      newState.toolStatus = null; // Clear tool status when text starts
                      break;

                    case 'explanation':
                      const explanationPayload = event.payload as ExplanationPayload;
                      newState.explanation = explanationPayload;
                      // Capture tokenUsage from explanation (when tools are called)
                      if (explanationPayload.tokenUsage) {
                        newState.tokenUsage = explanationPayload.tokenUsage;
                      }
                      break;

                    case 'done':
                      const donePayload = event.payload as DonePayload;
                      newState.status = 'done';
                      newState.conversationId = donePayload.conversationId;
                      newState.sources = donePayload.sources;
                      // Capture tokenUsage from done if not already from explanation
                      if (donePayload.tokenUsage && !newState.tokenUsage) {
                        newState.tokenUsage = donePayload.tokenUsage;
                      }
                      capturedNewState = newState;
                      break;

                    case 'error':
                      const errorPayload = event.payload as ErrorPayload;
                      newState.status = 'error';
                      newState.error = errorPayload.message;
                      options.onError?.(errorPayload.message);
                      break;
                  }

                  return newState;
                });
                if (capturedNewState !== null) {
                  finalState = capturedNewState;
                }
              } catch (parseError) {
                console.error('[useAiSearchStream] Error parsing event:', parseError, data);
              }
            }
          }
        }
      } catch (error) {
        if ((error as Error).name === 'AbortError') {
          // Request was aborted, don't update state
          return;
        }

        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        setState((prev) => ({
          ...prev,
          status: 'error',
          error: errorMessage,
        }));
        options.onError?.(errorMessage);
      }
    },
    [abort, options]
  );

  return {
    state,
    sendMessage,
    reset,
    abort,
    isStreaming: state.status === 'streaming' || state.status === 'connecting',
  };
}

export default useAiSearchStream;
